import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { CockpitShell, HouseFeedBadge } from "@/cockpit/CockpitShell";
import { TickerTalkCockpit } from "@/cockpit/talk/TickerTalkCockpit";
import { mapTalkDoc } from "@/cockpit/mapHouseFeed";

const TABS = [
  { id: "portfolio", label: "My Portfolio" },
  { id: "watchlists", label: "My Watchlists" },
  { id: "followed", label: "All Followed" },
  { id: "macro", label: "Macro & Sector" },
  { id: "all", label: "All House Feed" },
];

export default function TickerTalkPage() {
  const [rows, setRows] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("followed");
  const [chip, setChip] = useState("All");
  const [holdings, setHoldings] = useState([]);
  const [watchlist, setWatchlist] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/ticker-talk"),
      api.get("/watchlist").catch(() => ({ data: { tickers: [] } })),
      api.get("/portfolio/positions").catch(() => ({ data: [] })),
    ]).then(([talk, wl, pos]) => {
      setRows(talk.data.rows || []);
      setLastRun(talk.data.last_run || null);
      setWatchlist((wl.data.tickers || []).map((t) => String(t).toUpperCase()));
      const held = [];
      const stocks = pos.data?.stocks || [];
      const opts = pos.data?.options || [];
      [...stocks, ...opts].forEach((p) => {
        const t = String(p.ticker || "").toUpperCase();
        if (t && !held.includes(t)) held.push(t);
      });
      setHoldings(held);
    }).catch(() => setRows([])).finally(() => setLoading(false));
  }, []);

  const followed = useMemo(() => [...new Set([...holdings, ...watchlist])], [holdings, watchlist]);

  const visible = useMemo(() => {
    const inSet = (t) => {
      if (tab === "all") return true;
      if (tab === "portfolio") return holdings.includes(t);
      if (tab === "watchlists") return watchlist.includes(t);
      if (tab === "followed") return followed.includes(t);
      if (tab === "macro") return followed.includes(t) || holdings.includes(t);
      return true;
    };
    return (rows || []).filter((r) => inSet(r.ticker) && (chip === "All" || r.ticker === chip));
  }, [rows, tab, chip, holdings, watchlist, followed]);

  const chips = useMemo(() => {
    const base = tab === "portfolio" ? holdings : tab === "watchlists" ? watchlist : followed;
    return ["All", ...base];
  }, [tab, holdings, watchlist, followed]);

  const ticker = chip === "All" ? (visible[0]?.ticker || "") : chip;
  const selected = chip === "All" ? null : visible.find((r) => r.ticker === ticker);
  const data = useMemo(() => {
    if (selected) return { talk: mapTalkDoc(selected) };
    return { talk: mapTalkDoc({ ticker: "ALL", items: flattenAll(visible), tone: "mixed" }) };
  }, [selected, visible]);

  return (
    <CockpitShell
      title="Ticker Talk"
      blurb="Cached FinancialData.net headlines from Mongo. Tone is a headline keyword tag (or a vendor field if present) — not a SentiSense score and not market impact. Sector heatmap, oscillator, and A/D line stay off until a SentiSense source is opted in."
      ticker={ticker}
      onTicker={(t) => setChip(t)}
      tickers={visible.map((r) => r.ticker)}
      badge={<HouseFeedBadge />}
      lastRun={lastRun}
      testid="ticker-talk-page"
      extraFilters={(
        <>
          <div className="flex flex-wrap gap-1.5" data-testid="talk-tabs">
            {TABS.map((t) => (
              <button key={t.id} type="button" onClick={() => { setTab(t.id); setChip("All"); }}
                className="text-[11px] px-2 py-0.5 rounded-sm border"
                style={{
                  background: tab === t.id ? "var(--hoip-text-primary)" : "var(--hoip-card)",
                  color: tab === t.id ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
                  borderColor: "var(--hoip-border)",
                }}>{t.label}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="talk-chips">
            {chips.map((c) => (
              <button key={c} type="button" onClick={() => setChip(c)}
                className="text-[10px] px-2 py-0.5 rounded-sm border font-mono-data"
                style={{
                  background: chip === c ? "var(--hoip-hover)" : "transparent",
                  borderColor: "var(--hoip-border)",
                  color: "var(--hoip-text-secondary)",
                }}>{c}</button>
            ))}
          </div>
        </>
      )}
    >
      {loading ? (
        <div className="text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>Loading headlines…</div>
      ) : (
        <TickerTalkCockpit data={data} />
      )}
    </CockpitShell>
  );
}

function flattenAll(rows) {
  const items = [];
  (rows || []).forEach((r) => {
    (r.items || []).forEach((it) => items.push({ ...it, ticker: r.ticker }));
  });
  items.sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
  return items;
}
